/* @flow @format */

import { type ContextRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import * as React from 'react';
import qs from 'qs';

import type { ContestType, Dispatch, StoreState } from '../redux/types';
import { fetchAgreementBegin } from '../redux/reducer/term';
import { signinBegin } from '../redux/reducer/auth';
import { toJapanTime } from '../utils';
import SplashPage from './SplashPage';
import ZkaiPage from './ZkaiPage';

const taglines = {
  ice: 'Z会小学生コース/小学生タブレットコース 1年生',
  pizza: 'Z会小学生コース2年生',
  sushi: 'Z会小学生コース/小学生タブレットコース 3年生',
  'ice-a': 'Z会小学生コース/小学生タブレットコース 4年生',
  'pizza-a': 'Z会小学生コース/小学生タブレットコース 5年生',
  'sushi-a': 'Z会小学生コース/小学生タブレットコース 6年生',
};

const timelines = {
  ice: [
    {
      title: 'アイスクリームを つくろう',
      text: 'お客さんの注文に合わせたアイスクリームをロボットに作ってもらいます。ひとつひとつの指示の順番を考えることで、論理的思考力を育みます。',
    },
    {
      title: 'アイスクリームを せいりしよう',
      text: 'お客さんの注文に合わせてアイスクリームを整理していきます。要素を細分化することで、問題を分析する力を育みます。',
    },
    {
      title: 'まちがいを なおそう',
      text: 'お店でのコミュニケーションの中で生じたトラブルに対処していきます。失敗の原因を振り返り、修正することで、失敗に対応する力を身につけます。',
    },
    {
      title: 'じゅんばんに ならべよう',
      text: 'お客さんの要望に沿って、おすすめのアイスクリームを提案します。情報を構造的に整理し、必要な要素を抽出する力を育みます。',
    },
  ],
  pizza: [
    {
      title: 'ピザを とどけよう',
      text: 'お客さんの家までロボットにピザの配達をしてもらいます。物事を捉えやすい形にして考える抽象化と簡単なプログラムを学びます。',
    },
    {
      title: 'おなじ うごきを くりかえそう',
      text: '遠くのお客さんの家までロボットにピザの配達をしてもらいます。プログラムの規則性を見つけながら、繰り返しの概念を学びます。',
    },
    {
      title: 'はいたつする みちを えらぼう',
      text: '道路状況を読み取り、最適なルートを探します。抽象的な情報を活用しながら、課題を解決する力を身につけます。',
    },
    {
      title: '目じるしを つかって とどけよう',
      text: '目印を手がかりにピザを配達します。状況の変化に対応できるプログラムを作ることで、条件処理の概念を学びます。',
    },
  ],
  sushi: [],
  'ice-a': [],
  'pizza-a': [],
  'sushi-a': [],
};

type State = {
  loading: boolean,
};

type OwnProps = {|
  ...ContextRouter,
|};

type Props = {
  ...OwnProps,
  signinBegin: typeof signinBegin,
  fetchAgreementBegin: typeof fetchAgreementBegin,

  fetching: boolean,
  active: number | void,
  accepted: boolean,
  slug: string,
  contestType: ContestType,
};

class ProcSample extends React.Component<Props, State> {
  state = { loading: true };
  restoreTitle: ?Function;
  token: string;
  constructor(props: Props) {
    super(props);

    const urlParams = qs.parse(props.location.search, { ignoreQueryPrefix: true });
    if (!urlParams.token) {
      window.location = 'https://proc-error.sonyged.com/403.html';
    }
  }
  componentDidMount() {
    const oldTitle = document.title;
    this.restoreTitle = () => {
      document.title = oldTitle;
    };
    document.title = 'Z-pro 小学1年生';
    const urlParams = qs.parse(this.props.location.search, { ignoreQueryPrefix: true });
    this.token = typeof urlParams.token === 'string' ? urlParams.token : '';
    if (!this.token) {
      window.location = 'https://proc-error.sonyged.com/403.html';
      return;
    }

    const { fetchAgreementBegin, slug } = this.props;

    fetchAgreementBegin({
      token: this.token,
      ticketSlug: slug,
      termsOfUseVersion: 'proc/20220606',
      privacyPolicyVersion: 'proc/20220606',
    });

    this.setState({ loading: false });
  }

  componentWillUnmount() {
    if (this.restoreTitle) this.restoreTitle();
  }

  handleStartClick = () => {
    this.props.signinBegin({
      token: this.token,
      ticketSlug: this.props.slug,
      termsOfUseVersion: 'proc/20220606',
      privacyPolicyVersion: 'proc/20220606',
    });
  };

  render() {
    const { loading } = this.state;
    const { fetching, accepted, active, contestType } = this.props;

    if (loading || fetching) return <SplashPage />;
    return (
      <ZkaiPage
        kind={contestType}
        timelines={timelines[contestType]}
        tagline={taglines[contestType]}
        accepted={accepted}
        active={active}
        onStartClick={this.handleStartClick}
      />
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch, _: OwnProps) => ({
  signinBegin: (input) => dispatch(signinBegin(input)),
  fetchAgreementBegin: (input) => dispatch(fetchAgreementBegin(input)),
});

function mapStateToProps(state: StoreState, ownProps: OwnProps) {
  const {
    term: { privacyPolicySigned, termsOfUseSigned, timestamp },
  } = state;

  const slug: string = ownProps.match.params.slug || '';
  const contestType = slug[slug.length - 1] === '1' ? 'ice' : 'pizza';

  const fetching = !timestamp;
  const accepted = privacyPolicySigned && termsOfUseSigned;
  let active = undefined;
  if (!fetching) {
    const japanTime = toJapanTime(new Date(timestamp));
    // Date.getMonth() return value in [0, 11]
    const month = japanTime.getUTCMonth() + 1;
    if (1 <= month && month <= 3) {
      active = 3;
    } else if (4 <= month && month <= 6) {
      active = 0;
    } else if (7 <= month && month <= 9) {
      active = 1;
    } else if (10 <= month && month <= 12) {
      active = 2;
    }
  }

  return { fetching, accepted, active, slug, contestType };
}

export default connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps)(ProcSample);
